'use client';

import { useEffect, useRef, useState } from 'react';
import {
  LOCKOUT_MODAL_TIMEOUT,
  TRANSCRIPT_LOCKOUT_MODAL_LOCAL_STORAGE_KEY,
} from '../../constants/lockout';
import dynamic from 'next/dynamic';

const LockoutModalContent = dynamic(() => import('../LockoutModalContent'));
const Modal = dynamic(() => import('../Modal'));

/**
 * TranscriptModal .
 */
const TranscriptModal = () => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollY = useRef<number>(0);
  const hasTriggered = useRef<boolean>(false);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const modalSeen = localStorage.getItem(
      TRANSCRIPT_LOCKOUT_MODAL_LOCAL_STORAGE_KEY,
    );
    if (modalSeen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        const currentScrollY = window.scrollY;
        const isScrollingDown = currentScrollY > lastScrollY.current;
        lastScrollY.current = currentScrollY;
        if (entry.isIntersecting || isScrollingDown) {
          if (!timeoutRef.current && !hasTriggered.current) {
            timeoutRef.current = setTimeout(() => {
              setIsModalOpen(true);
              localStorage.setItem(
                TRANSCRIPT_LOCKOUT_MODAL_LOCAL_STORAGE_KEY,
                'true',
              );
              hasTriggered.current = true;
            }, LOCKOUT_MODAL_TIMEOUT);
          }
        } else if (!isScrollingDown) {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          hasTriggered.current = false;
        }
      },
      { threshold: 0 },
    );

    observer.observe(target);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      observer.disconnect();
    };
  }, [isModalOpen]);

  return (
    <>
      <div
        ref={targetRef}
        id="transcript-block"
        data-testid="transcript-block"
      />
      {isModalOpen && (
        <Modal
          role="dialog"
          isVisible={isModalOpen}
          hide={() => setIsModalOpen(false)}
          overlayOpacity={0.2}
          className="rounded-[10px]"
          _className="!overflow-visible !max-h-[85vh] !max-w-[600px] !w-full"
        >
          <LockoutModalContent
            dataTestId="transcript-lockout-modal"
            onClose={() => setIsModalOpen(false)}
            closeButton={true}
          />
        </Modal>
      )}
    </>
  );
};

export default TranscriptModal;
